import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import { PhoneFrame } from "@/components/mentor-profile/phone-frame";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useMentorProfileReviewByMentorId,
  type MentorProfileReviewStatus,
} from "@/lib/api/mentor-profile";
import { Button } from "@somethings-ui/web";
import { Link } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { Loader2 } from "lucide-react";

function statusCopy(status: MentorProfileReviewStatus) {
  switch (status) {
    case "approved":
      return "Published matching profile";
    case "submitted":
    case "in_review":
      return "Awaiting clinical review";
    case "changes_requested":
      return "Changes requested — showing current draft";
    case "in_progress":
    case "invited":
      return "Still in onboarding — showing current draft";
    case "rejected":
      return "Rejected — showing last draft";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

interface MatchingProfilePreviewDialogProps {
  mentorId: string;
  mentorLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MatchingProfilePreviewDialog({
  mentorId,
  mentorLabel,
  open,
  onOpenChange,
}: MatchingProfilePreviewDialogProps) {
  const { data: review, isPending, isError, error } =
    useMentorProfileReviewByMentorId(mentorId, open);

  const isNotFound = isAxiosError(error) && error.response?.status === 404;
  const previewDraft = review?.published_snapshot ?? review?.draft ?? null;
  const canOpenReview =
    review &&
    (review.status === "submitted" || review.status === "in_review");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-full max-w-md overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Matching profile</DialogTitle>
          <DialogDescription>
            {review
              ? `${mentorLabel} · ${statusCopy(review.status)}`
              : `How ${mentorLabel} appears on the matching card.`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-2">
          {isPending ? (
            <div className="flex items-center gap-2 py-16 font-body text-sm text-text-secondary">
              <Loader2 className="size-4 animate-spin" />
              Loading profile…
            </div>
          ) : isNotFound ? (
            <p className="py-12 text-center font-body text-sm text-text-secondary">
              This mentor hasn&apos;t started a matching profile yet.
            </p>
          ) : isError || !previewDraft ? (
            <p className="py-12 text-center font-body text-sm text-states-error">
              Couldn&apos;t load this matching profile.
            </p>
          ) : (
            <PhoneFrame className="origin-top scale-[0.85]">
              <MatchingProfilePreviewCard draft={previewDraft} />
            </PhoneFrame>
          )}

          {canOpenReview && (
            <Link
              to="/users/mentor-profile-reviews/$reviewId"
              params={{ reviewId: review.id }}
              onClick={() => onOpenChange(false)}
            >
              <Button size="sm" fullWidth={false}>
                Open review
              </Button>
            </Link>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
